import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import routes from "../../../../routes/routes";
import { useKeyHandoverStepCounter } from "../hooks/useKeyHandoverStepCounter";
import KeyHandoverGuestDetailsStep from "../steps/KeyHandoverGuestDetailsStep";
import { useAppDispatch } from "../../../../redux/useDispatch";
import { recipientFirstNameChanged, recipientLastNameChanged, recipientEmailChanged, recipientPhoneChanged } from "../../../../redux/slice/bookingCreation/bookingCreationSlice";
import type { RootState } from "../../../../redux/store";
import KeyHandoverNavigateNextAction from "../common/util/KeyHandoverNavigateNextAction";
import { useLocationHydration } from "../hooks/useLocationHydration";
import { useServiceHydration } from "../hooks/useServiceHydration";
import { useKeyHandoverPageInit } from "../hooks/useKeyHandoverPageInit";
import { KeyHandoverPageLayout } from "../components/KeyHandoverPageLayout";
import { KeyHandoverContentWrapper } from "../components/KeyHandoverContentWrapper";

/**
 * Content component that handles hydration within proper context providers.
 */
const GuestDetailsContent: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const recipientDataHydratedRef = useRef(false);
  
  const recipientData = useSelector((s: RootState) => s.bookingCreation.recipientData);
  
  // Hydrate state from URL
  useLocationHydration();
  const serviceHydrated = useServiceHydration();

  // Hydrate recipient data from query parameters (only once)
  useEffect(() => {
    if (recipientDataHydratedRef.current) return;
    
    const params = new URLSearchParams(location.search);
    if (recipientData.firstName === "" && params.get("recipientFirstName")) {
      dispatch(recipientFirstNameChanged({ firstName: params.get("recipientFirstName")! }));
    }
    if (recipientData.lastName === "" && params.get("recipientLastName")) {
      dispatch(recipientLastNameChanged({ lastName: params.get("recipientLastName")! }));
    }
    if (recipientData.email === "" && params.get("recipientEmail")) {
      dispatch(recipientEmailChanged({ email: params.get("recipientEmail")! }));
    }
    if (recipientData.phone === "" && params.get("recipientPhone")) {
      dispatch(recipientPhoneChanged({ phone: params.get("recipientPhone")! }));
    }
    
    recipientDataHydratedRef.current = true;
  }, [location.search, recipientData.firstName, recipientData.lastName, recipientData.email, recipientData.phone, dispatch]);

  // Persist recipient data to URL
  useEffect(() => {
    if (!serviceHydrated) return;
    
    const params = new URLSearchParams(location.search);
    let mutated = false;
    
    const setOrDeleteParam = (key: string, value: string) => {
      if (value) {
        if (params.get(key) !== value) {
          params.set(key, value);
          mutated = true;
        }
      } else {
        if (params.has(key)) {
          params.delete(key);
          mutated = true;
        }
      }
    };
    
    setOrDeleteParam("recipientFirstName", recipientData.firstName);
    setOrDeleteParam("recipientLastName", recipientData.lastName);
    setOrDeleteParam("recipientEmail", recipientData.email);
    setOrDeleteParam("recipientPhone", recipientData.phone);
    
    if (mutated) {
      navigate({ pathname: location.pathname, search: `?${params.toString()}` }, { replace: true });
    }
  }, [serviceHydrated, recipientData.firstName, recipientData.lastName, recipientData.email, recipientData.phone, navigate, location.pathname, location.search]);

  return <KeyHandoverGuestDetailsStep />;
};

const KeyHandoverGuestDetailsPage: React.FC = () => {
  const { t } = useTranslation();
  useKeyHandoverStepCounter("guest");

  const selectedGroup = useSelector((s: RootState) => s.bookingCreation.selectedGroup);
  const recipientDataValidation = useSelector((s: RootState) => s.bookingCreation.recipientDataValidation);

  // Initialize page
  useKeyHandoverPageInit();

  // Validation gating for Next
  const guestValid = Boolean(
    recipientDataValidation.firstName &&
    recipientDataValidation.lastName &&
    recipientDataValidation.email
  );

  const bottomActions = selectedGroup ? (
    <div className="w-full">
      <KeyHandoverNavigateNextAction
        to={routes.keyHandoverSummaryPath}
        enabled={Boolean(selectedGroup) && guestValid}
        bounceKey={guestValid ? selectedGroup?.id ?? null : null}
      />
    </div>
  ) : undefined;

  return (
    <KeyHandoverPageLayout
      headerText={t("book_location.keys.guest_details.header_text", {
        defaultValue: "Guest Details",
      })}
      backTo={routes.keyHandoverHostDetailsPath}
      bottomActions={bottomActions}
    >
      <KeyHandoverContentWrapper>
        <GuestDetailsContent />
      </KeyHandoverContentWrapper>
    </KeyHandoverPageLayout>
  );
};

export default KeyHandoverGuestDetailsPage;
